import { useCallback } from "react";

import type { SnackSettings_SnackSettingsQuery } from "@/lib/gql/__generated__/graphql";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { toGuestPageVisibilitySettingsVariables } from "@/components/features-new/guest-page-visibility";

import { SnackSettingsUpdate_SnackSettingsPage_Mutation } from "./mutation";
import { viewModelToInput as viewModelToSnackBlocksInput } from "../../../usecase/use-create-snack-layout-settings-form-model/converter/view-model-to-input";
import { viewModelToInput as viewModelToSnackSettingsInput } from "../../../usecase/use-create-snack-settings-form-model/converter/view-model-to-input";

import type { DesignSnackBlocksFormModel } from "../../../usecase/use-create-snack-layout-settings-form-model/type";
import type { DesignSnackSettingsFormModel } from "../../../usecase/use-create-snack-settings-form-model/type";

type Props = {
	resetSnackSettingsViewModel: () => void;
	resetSnackLayoutSettingsViewModel: () => void;
};

export const useSnackSettingsUpdateMutation = ({
	resetSnackLayoutSettingsViewModel,
	resetSnackSettingsViewModel,
}: Props) => {
	const { mutationFunc } = useCustomMutation(SnackSettingsUpdate_SnackSettingsPage_Mutation);
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const onUpdate = useCallback(
		async ({
			snackSettingsViewModel,
			snackLayoutSettingsViewModel,
			guestPageVisibilitySettings,
		}: {
			snackSettingsViewModel: DesignSnackSettingsFormModel["ViewModel"];
			snackLayoutSettingsViewModel: DesignSnackBlocksFormModel["ViewModel"];
			/** 取得した公開設定（スナック以外のページはこの値のまま送る） */
			guestPageVisibilitySettings: SnackSettings_SnackSettingsQuery["guestPageVisibilitySettings"];
		}) => {
			showLoadingScreenCallback();

			const snackSettingsInput = viewModelToSnackSettingsInput({ viewModel: snackSettingsViewModel });
			const snackBlocksInput = viewModelToSnackBlocksInput({ viewModel: snackLayoutSettingsViewModel });
			const guestPageVisibilitySettingsVariables = toGuestPageVisibilitySettingsVariables({
				saved: guestPageVisibilitySettings,
				visibilityKey: "snackVisibility",
				visibility: snackSettingsViewModel.guestPageVisibility.value,
			});

			const { errors } = await mutationFunc({
				variables: {
					snackSettingsInput,
					snackBlocksInput,
					...guestPageVisibilitySettingsVariables,
				},
			}).finally(hideLoadingScreenCallback);

			if (!errors) {
				resetSnackLayoutSettingsViewModel();
				resetSnackSettingsViewModel();

				notifyUserEventInfo({
					title: "スナックの設定を保存しました",
				});
			}
		},
		[
			hideLoadingScreenCallback,
			mutationFunc,
			resetSnackLayoutSettingsViewModel,
			resetSnackSettingsViewModel,
			showLoadingScreenCallback,
		],
	);

	return {
		onUpdate,
	};
};
